import { Component, Input, inject, signal, WritableSignal } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";

import { GOLD_MULTIPLIER } from "@bitwarden/web-vault/app/models/constants/credits.constants";

/**
 * Confirmation dialog body for the Platinum → Gold conversion flow (Step 5).
 *
 * Confirm-only: it shows the amount being converted and the client-side Gold estimate, then
 * closes with `true` (confirmed) or `false` (cancelled). It does **not** call
 * `convertCredits()` and does **not** await anything — the host (`PointsPurchaseComponent`'s
 * "Use Platinum Credits" tab) owns the network call via `afterClosed()` once this dialog closes
 * (Locked Decisions #4/#18/#19).
 *
 * `amount` arrives as a plain `@Input()` bound through `DialogWrapperData.componentInputs`
 * (ngComponentOutlet `inputs`), not via `MAT_DIALOG_DATA`.
 */
@Component({
  selector: "app-conversion-confirmation",
  templateUrl: "./conversion-confirmation.component.html",
})
export class ConversionConfirmationComponent {
  /** Platinum amount being converted, supplied by the drawer. */
  @Input() amount = 0;

  // Present when rendered inside the DialogWrapperComponent; optional so the component can still
  // be instantiated in isolation (tests / non-dialog hosts).
  private dialogRef = inject<MatDialogRef<unknown>>(MatDialogRef, { optional: true });

  /**
   * Double-submit guard local to this dialog (Locked Decision #17) — blocks a second concurrent
   * click on Confirm. Unrelated to the drawer's `converting` signal (Locked Decision #19), which
   * guards the actual network call.
   */
  protected readonly confirming: WritableSignal<boolean> = signal(false);

  /** Client-side Gold estimate for display (Locked Decision #7). */
  protected get goldEstimate(): number {
    return this.amount * GOLD_MULTIPLIER;
  }

  /** Confirm: close immediately with `true`. Does not call the service or await anything. */
  protected onConfirm(): void {
    if (this.confirming()) {
      return;
    }
    this.confirming.set(true);
    this.dialogRef?.close(true);
  }

  /** Cancel: close with a falsy result — the drawer treats this as "user backed out". */
  protected onCancel(): void {
    this.dialogRef?.close(false);
  }
}
